/**
 * Created by piotr.pozniak@thebeaverhead.com on 21/08/2021.
 */
import EventsStrategy from "./EventsStrategy";
import AxiosConfig from "../../AxiosConfig";
import { RRule, RRuleSet } from "rrule";
import { lcFirst } from "../../helpers/string";
import { cleanRRule } from "../../helpers/calendar";
import { getLinksFromDescription, linkify } from "../../helpers/widgetItems";

const parseISO8601Basic = (inputStr) => {
  const input = inputStr.trim();

  if (input.length === 8) {
    const [y, m, d] = [input.slice(0, 4), input.slice(4, 6), input.slice(6, 8)];
    return new Date(`${y}-${m}-${d}T00:00:00Z`);
  }

  // 15 chars without Z, or 16 with Z: YYYYMMDDTHHmmss[Z]
  const bare = input.replace(/Z$/, "");
  if (bare.length === 15 && bare[8] === "T") {
    const y = bare.slice(0, 4);
    const mo = bare.slice(4, 6);
    const d = bare.slice(6, 8);
    const hh = bare.slice(9, 11);
    const mm = bare.slice(11, 13);
    const ss = bare.slice(13, 15);
    // Return as UTC — caller must ensure dtstart is also UTC
    return new Date(`${y}-${mo}-${d}T${hh}:${mm}:${ss}Z`);
  }

  return new Date(NaN);
};
const sanitizeValue = (value, defaultValue) => {
  return value && !Array.isArray(value) ? value : defaultValue;
};

const isSearchableLocationString = (locationName) => {
  if (!locationName) {
    return false;
  }

  const lower = locationName.toLowerCase();

  const omitWords = ["http", "www", "tba"];
  if (omitWords.some((word) => lower.includes(word))) {
    return false;
  }

  const commasCount = (locationName.match(/,/g) || []).length;
  if (commasCount < 2 || commasCount > 5) {
    return false;
  }

  return true;
};

const parseRuleDates = (rrule, rule) => {
  if (!rrule) {
    return [];
  }
  const dates = [];

  for (const ruleLine of rrule) {
    const regex = new RegExp(rule);
    if (regex.test(ruleLine)) {
      let cleaned = ruleLine;

      // Handle TZID format: EXDATE;TZID=Europe/Athens:20260306T143000
      cleaned = cleaned.replace(
        /;TZID=[^:]+:(\d{8}T\d{6})/g,
        (_, group1) => `${group1}Z`
      );

      // Handle VALUE=DATE format: EXDATE;VALUE=DATE:20260207
      cleaned = cleaned.replace(/;VALUE=DATE:(\d{8})/g, (_, group1) => group1);

      // Strip the rule prefix (EXDATE or RDATE)
      cleaned = cleaned.replace(rule, "");

      dates.push(cleaned);
    }
  }

  return dates;
};

/**
 *
 * @param rrule
 * @returns {Date[]|null}
 */
const parseRecurrenceSeries = (startDate, rrule) => {
  if (!rrule) {
    return [null, null];
  }

  const exDates = parseRuleDates(rrule, "EXDATE");
  const rdates = parseRuleDates(rrule, "RDATE");
  const cleanRuleLines = rrule.filter((i) => !/(EXDATE|RDATE)/.test(i));

  if (!cleanRuleLines.length) {
    return [null, null];
  }

  try {
    const options = RRule.parseString(cleanRuleLines.join("\n"));
    options.dtstart = new Date(startDate);

    const rule = cleanRRule(options);
    const rruleSet = new RRuleSet();
    rruleSet.rrule(rule);

    rruleSet.rrule(rule);

    const dtStartTime = options.dtstart.toISOString().slice(11); // "HH:MM:SS.sssZ"

    const toDateKey = (d) => {
      // Parse just the date portion in UTC from the ISO8601Basic string
      const iso = parseISO8601Basic(d);
      return iso.toISOString().slice(0, 10);
    };

    const buildAtTime = (dateKey) => new Date(`${dateKey}T${dtStartTime}`);

    // Get rrule occurrences indexed by date
    const allExAndRDates = [...exDates, ...rdates].map(parseISO8601Basic);

    const occurrencesByDate = new Map();

    if (allExAndRDates.length) {
      // Only look up occurrences around the dates we actually need to match
      for (const targetDate of allExAndRDates) {
        const from = new Date(targetDate);
        from.setDate(from.getDate() - 1);
        const to = new Date(targetDate);
        to.setDate(to.getDate() + 1);

        for (const occ of rruleSet.between(from, to, true)) {
          occurrencesByDate.set(occ.toISOString().slice(0, 10), occ);
        }
      }
    }

    for (const exDate of exDates) {
      const dateKey = toDateKey(exDate);
      const occurrence = occurrencesByDate.get(dateKey);
      if (occurrence) {
        rruleSet.exdate(occurrence); // exact same object = guaranteed valueOf match
      }
    }

    for (const rdate of rdates) {
      const dateKey = toDateKey(rdate);
      rruleSet.rdate(buildAtTime(dateKey));
    }

    for (const rdate of rdates) {
      const d = parseISO8601Basic(rdate);
      d.setUTCHours(
        options.dtstart.getUTCHours(),
        options.dtstart.getUTCMinutes(),
        options.dtstart.getUTCSeconds(),
        0
      );
      rruleSet.rdate(d);
    }

    return [rruleSet.toString(), rule.toText()];
  } catch (e) {
    console.error(e);
    return [null, null];
  }
};

class GoogleEventsStrategy extends EventsStrategy {
  processData(data, integration, modelData) {
    return data.map((i) => {
      const event = i.data;

      const startDate = i.start_date;
      const endDate = i.end_date;

      const originalStartDate = i.data?.originalStartTime;

      const eventProfileStartDate =
        originalStartDate?.date || originalStartDate?.dateTime;

      const eventColor = i.color?.data || null;

      const coverImagePath =
        i.attachments && i.attachments.length
          ? i.attachments[0].data.path
          : null;

      const coverImage = coverImagePath
        ? coverImagePath.includes("http")
          ? coverImagePath
          : AxiosConfig.getEndpointAddress() + `/${coverImagePath}`
        : null;

      const oldRule = event?.recurrence;
      const rruleFromEventProfile = i?.event_profile?.data?.recurrence;
      const rrule = rruleFromEventProfile || oldRule;

      const [recurrence_series, rruleDesc] = parseRecurrenceSeries(
        eventProfileStartDate,
        rrule
      );

      const isAllDayEvent = sanitizeValue(event.start.date, null)
        ? true
        : false;

      const description = linkify(
        event.description || "" //.replaceAll("</", "\n</") // TODO verify
      );

      const descriptionLinks = getLinksFromDescription(description);

      const tagsData = Object.values(i.data?.tags || []);

      const tags = tagsData.map((tag) => ({
        label: tag,
      }));

      const organizer = {
        name: sanitizeValue(
          event.organizer.displayName
            ? event.organizer.displayName
            : event.organizer.email,
          null
        ),
        email: sanitizeValue(event.organizer.email, null),
      };

      const ctas = {
        linkFirst: descriptionLinks[0] || null,
        linkLast: descriptionLinks[1] || null,
        eventPage: null,
        form: null,
        organizer:
          organizer && organizer.email
            ? {
                url: `mailto:${organizer.email}`,
                label: "Contact organizer",
              }
            : null,
      };

      const eventPlace =
        i?.place?.data && i.place.data.name.startsWith("places/")
          ? i.place.data
          : null;

      const place = eventPlace
        ? {
            displayName: eventPlace.displayName?.text || "",
            summary: eventPlace.editorialSummary?.text || "",
            iconBgColor: eventPlace.iconBackgroundColor,
            iconURi: eventPlace.iconMaskBaseUri,
            rating: eventPlace.rating,
            userRatingCount: eventPlace.userRatingCount,
          }
        : null;

      if (place && eventPlace?.photos) {
        place.photos = eventPlace.photos.map((photo) => {
          return {
            url: photo?.path.includes("http")
              ? photo.path
              : AxiosConfig.getEndpointAddress() + `/${photo.path}`,
            caption: photo?.authorAttributions[0]?.displayName,
          };
        });
      }

      return {
        id: i.id,
        ics_id: i.platform_event_id,
        slug: i.short_slug,
        description,
        name: event.summary,
        start_time: startDate,
        end_time: endDate,
        //timezone: eventProfile.timezone, todo
        allDay: isAllDayEvent,
        timezone: "UTC", //event.start.timeZone || i.calendar.data.timeZone || "UTC",
        recurrence_description: rruleDesc ? lcFirst(rruleDesc) : null,
        recurrence_series,
        //duration: data.event_duration,
        //modified_by: eventProfile.modifier["$"],
        //integration_event_id: i.platform_event_id,
        display_location: event.location,
        cover_image: coverImage,
        //is_private: eventProfile.public_calendar_listed !== "true",
        location: {
          city: " ",
          /*  line_1: sanitizeValue(eventProfile.location.line_1, null),
            line_2: sanitizeValue(eventProfile.location.line_2, null),*/
          name: event.location,
          hasSearchableLocation: eventPlace
            ? true
            : isSearchableLocationString(event.location), // sanitizeValue(eventProfile.location.name, null),
          /*zip: sanitizeValue(eventProfile.location.zip, null),
          street_address: sanitizeValue(
            eventProfile.location.street_address,
            null
          ),*/
        },
        organizer,
        contact: {
          phone: "",
        },
        accentColor: eventColor
          ? eventColor.background
          : i.calendar.data.backgroundColor,
        fontColor: eventColor
          ? eventColor.foreground
          : i.calendar.data.foregroundColor,
        ctas,
        place,
        is_featured: i.is_featured,
        tags,
      };
    });
  }
}

export default GoogleEventsStrategy;
